import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircle2Icon, CircleAlertIcon, CreditCardIcon, LockKeyholeIcon } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { createCheckoutSession, redirectToCheckout } from '../../../utils/stripe';
import { getCheckoutUrl, getStudyUrl, getStudyProgram } from '../../../utils/studyAccess';
import { useStudyMembership } from '../../../utils/useStudyMembership';
const FONT = '"Poppins", "Inter", system-ui, sans-serif';
export function Checkout() {
  const {
    user,
    loading: authLoading
  } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const program = getStudyProgram(searchParams.get('product'));
  const checkoutState = searchParams.get('state');
  const membership = useStudyMembership(program?.id);
  const [startingCheckout, setStartingCheckout] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentUrl = `${location.pathname}${location.search}`;
  if (!program) {
    return <InvalidCheckout data-id="element-1874" />;
  }
  if (authLoading) {
    return <LoadingState data-id="element-1875" />;
  }
  if (!user) {
    navigate('/login', {
      state: {
        from: currentUrl
      },
      replace: true
    });
    return <LoadingState data-id="element-1876" />;
  }
  const startCheckout = async () => {
    setStartingCheckout(true);
    setError(null);
    try {
      const lineItem = program.stripePriceId ? {
        priceId: program.stripePriceId,
        quantity: 1
      } : {
        lookupKey: program.lookupKey,
        quantity: 1
      };
      const baseUrl = window.location.origin;
      const session = await createCheckoutSession({
        lineItems: [lineItem],
        successUrl: `${baseUrl}${getCheckoutUrl(program.id, 'success')}`,
        cancelUrl: `${baseUrl}${getCheckoutUrl(program.id, 'cancelled')}`
      });
      redirectToCheckout(session.url);
    } catch (checkoutError) {
      setError(checkoutError instanceof Error ? checkoutError.message : 'We could not start checkout. Please try again.');
      setStartingCheckout(false);
    }
  };
  const paid = membership.hasAccess;
  const waitingForOrder = checkoutState === 'success' && !paid;
  return <div className="min-h-screen w-full bg-[#F7F9FC] px-5 py-12 text-[#0F172A] sm:px-8 md:py-20" style={{
    fontFamily: FONT
  }} data-id="element-1877">
      <main className="mx-auto max-w-3xl" data-id="element-1878">
        <Link to={paid ? getStudyUrl(program.id) : `/${program.id === 'rd-ready-rd-exam' ? 'rd-exam' : 'cnsc-exam'}`} className="inline-flex items-center gap-2 text-sm font-semibold text-[#52627E] transition-colors hover:text-[#2563EB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]" data-id="element-1879">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-1880" />
          Back to {program.shortName}
        </Link>

        <section className="mt-7 overflow-hidden rounded-3xl border border-[#E2E8F0] bg-white shadow-[0_12px_36px_rgba(15,23,42,.08)]" data-id="element-1881">
          <div className="bg-[#0F172A] px-6 py-7 text-white sm:px-9" data-id="element-1882">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/65" data-id="element-1883">
              Secure checkout
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl" data-id="element-1884">
              Unlock {program.name}
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/75" data-id="element-1885">
              One-time purchase, tied to this RD Ready account.
            </p>
          </div>

          <div className="p-6 sm:p-9" data-id="element-1886">
            {paid ? <div className="rounded-2xl border border-[#A7E3DA] bg-[#F0FBF9] p-6" data-id="element-1887">
                <CheckCircle2Icon className="h-7 w-7 text-[#0F9E8E]" data-id="element-1888" />
                <h2 className="mt-4 text-xl font-bold" data-id="element-1889">
                  Your program is unlocked.
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-[#52627E]" data-id="element-1890">
                  This account has paid access to {program.name}.
                </p>
                <Link to={getStudyUrl(program.id)} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0F9E8E] px-5 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F9E8E] focus-visible:ring-offset-2" data-id="element-1891">
                  Open study <ArrowRightIcon className="h-4 w-4" data-id="element-1892" />
                </Link>
              </div> : waitingForOrder ? <div className="rounded-2xl border border-[#BFDBFE] bg-[#F5F8FF] p-6" data-id="element-1893">
                <CheckCircle2Icon className="h-7 w-7 text-[#2563EB]" data-id="element-1894" />
                <h2 className="mt-4 text-xl font-bold" data-id="element-1895">Payment received.</h2>
                <p className="mt-2 text-sm leading-relaxed text-[#52627E]" data-id="element-1896">
                  We are confirming your order and unlocking your study space.
                  This page updates automatically when the paid order is
                  available.
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB]" data-id="element-1897">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#2563EB]/30 border-t-[#2563EB]" data-id="element-1898" />
                  Confirming access…
                </span>
              </div> : <>
                {checkoutState === 'cancelled' && <div role="status" className="mb-6 flex gap-3 rounded-2xl border border-[#FDE68A] bg-[#FFFBEB] p-4 text-sm leading-relaxed text-[#92400E]" data-id="element-1899">
                    <CircleAlertIcon className="mt-0.5 h-5 w-5 shrink-0" data-id="element-1900" />
                    Checkout was cancelled. Your program has not been purchased.
                  </div>}

                <div className="flex items-start justify-between gap-5 border-b border-[#E2E8F0] pb-6" data-id="element-1901">
                  <div data-id="element-1902">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#64748B]" data-id="element-1903">
                      {program.name}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-[#52627E]" data-id="element-1904">
                      Quizzes, flashcards, and focused review access.
                    </p>
                  </div>
                  <p className="shrink-0 text-3xl font-extrabold" data-id="element-1905">$150</p>
                </div>

                <div className="mt-6 flex items-start gap-3 text-sm leading-relaxed text-[#52627E]" data-id="element-1906">
                  <LockKeyholeIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#0F9E8E]" data-id="element-1907" />
                  Your purchase is linked to{' '}
                  {user.email ?? 'your signed-in account'}. You can return to
                  your program from any device after signing in.
                </div>

                {error && <p role="alert" className="mt-6 rounded-xl border border-[#FBD5D0] bg-[#FEF2F2] p-4 text-sm font-medium text-[#B91C1C]" data-id="element-1908">
                    {error}
                  </p>}

                <button type="button" onClick={startCheckout} disabled={startingCheckout} className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2563EB] px-6 py-4 text-sm font-bold text-white transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0" data-id="element-1909">
                  {startingCheckout ? <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" data-id="element-1910" />
                      Opening secure checkout…
                    </> : <>
                      <CreditCardIcon className="h-4 w-4" data-id="element-1911" />
                      Continue to secure checkout · $150
                    </>}
                </button>
                <p className="mt-4 text-center text-xs leading-relaxed text-[#94A3B8]" data-id="element-1912">
                  Payment is handled by Stripe. Access is granted after a paid
                  order is recorded for this program.
                </p>
              </>}
          </div>
        </section>
      </main>
    </div>;
}
function LoadingState() {
  return <div className="flex min-h-[70vh] items-center justify-center bg-white" data-id="element-1913">
      <span className="h-7 w-7 animate-spin rounded-full border-2 border-[#2563EB] border-t-transparent" data-id="element-1914" />
    </div>;
}
function InvalidCheckout() {
  return <div className="flex min-h-[70vh] items-center justify-center bg-[#F7F9FC] px-5 text-center" data-id="element-1915">
      <div className="max-w-md rounded-3xl border border-[#E2E8F0] bg-white p-8 shadow-sm" data-id="element-1916">
        <h1 className="text-2xl font-bold text-[#0F172A]" data-id="element-1917">
          Choose a study program
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-[#52627E]" data-id="element-1918">
          Select RD Exam Prep or CNSC Exam Prep to continue to checkout.
        </p>
        <Link to="/" className="mt-6 inline-flex rounded-full bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white" data-id="element-1919">
          Explore programs
        </Link>
      </div>
    </div>;
}